/**
 * PlayerCardTable - Sortable table of player card statistics
 *
 * Uses the same styling pattern as PlayerList for consistency.
 */
import React, { useState, useMemo } from 'react'
import type { PlayerCardRow, CardWindowKey, TeamLite, TrendDirection } from '../types'

interface PlayerCardTableProps {
  players: PlayerCardRow[]
  window: CardWindowKey
  team: TeamLite
  showXI: boolean // true = XI, false = bench
  showPer90: boolean // true = show per/90 rates, false = show totals
}

type SortKey = 'name' | 'position' | 'apps' | 'minutes' | 'fouls_committed' | 'fouls_drawn' | 'yellows' | 'reds'
type SortDir = 'asc' | 'desc'

type ColumnDef = { key: SortKey; label: string; per90Label?: string }

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Player' },
  { key: 'position', label: 'Pos' },
  { key: 'apps', label: 'Apps' },
  { key: 'minutes', label: 'Mins' },
  { key: 'fouls_committed', label: 'Fouls', per90Label: 'Fouls/90' },
  { key: 'fouls_drawn', label: 'Drawn', per90Label: 'Drawn/90' },
  { key: 'yellows', label: 'Yel', per90Label: 'Yel/90' },
  { key: 'reds', label: 'Red', per90Label: 'Red/90' },
]

function TrendArrow({ trend, show }: { trend: TrendDirection | null; show: boolean }) {
  if (!show || !trend) return null

  if (trend === 'up') {
    return <span style={{ color: 'var(--green)', marginLeft: 4 }}>▲</span>
  } else if (trend === 'down') {
    return <span style={{ color: 'var(--red)', marginLeft: 4 }}>▼</span>
  }
  return null
}

/** Calculate per/90 rate from total and minutes */
function calcPer90(total: number, minutes: number): number | null {
  if (minutes < 45) return null // Need at least 45 mins for meaningful rate
  return (total / minutes) * 90
}

function getSortValue(player: PlayerCardRow, key: SortKey, window: CardWindowKey, showPer90: boolean): string | number {
  const windowData = player.windows[window]
  const minutes = windowData?.minutes ?? 0

  switch (key) {
    case 'name':
      return player.name.toLowerCase()
    case 'position':
      return player.position || 'Z'
    case 'apps':
      return windowData?.matches ?? 0
    case 'minutes':
      return minutes
    case 'fouls_committed': {
      const total = windowData?.fouls_committed ?? 0
      if (showPer90) {
        const per90 = windowData?.fouls_per_90 ?? calcPer90(total, minutes)
        return per90 ?? -1 // -1 sorts nulls to bottom
      }
      return total
    }
    case 'fouls_drawn': {
      const total = windowData?.fouls_drawn ?? 0
      if (showPer90) {
        const per90 = calcPer90(total, minutes)
        return per90 ?? -1
      }
      return total
    }
    case 'yellows': {
      const total = windowData?.yellows ?? 0
      if (showPer90) {
        const per90 = windowData?.yellows_per_90 ?? calcPer90(total, minutes)
        return per90 ?? -1
      }
      return total
    }
    case 'reds': {
      const total = windowData?.reds ?? 0
      if (showPer90) {
        const per90 = calcPer90(total, minutes)
        return per90 ?? -1
      }
      return total
    }
    default:
      return 0
  }
}

export function PlayerCardTable({
  players,
  window,
  team,
  showXI,
  showPer90,
}: PlayerCardTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('yellows')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Filter players by XI/bench
  const filteredPlayers = useMemo(() => {
    return players.filter((p) => p.is_xi === showXI && p.team_id === team.id)
  }, [players, showXI, team.id])

  // Sort players
  const sortedPlayers = useMemo(() => {
    return [...filteredPlayers].sort((a, b) => {
      const aVal = getSortValue(a, sortKey, window, showPer90)
      const bVal = getSortValue(b, sortKey, window, showPer90)

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }

      const aNum = Number(aVal)
      const bNum = Number(bVal)
      return sortDir === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [filteredPlayers, sortKey, sortDir, window, showPer90])

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const getSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return ''
    return sortDir === 'asc' ? ' ▲' : ' ▼'
  }

  if (filteredPlayers.length === 0) {
    return null
  }

  const showTrend = window !== 'season'

  /** Format per/90 value or show '-' if insufficient minutes */
  const formatPer90 = (total: number, minutes: number, existingPer90?: number | null): string => {
    const per90 = existingPer90 ?? calcPer90(total, minutes)
    if (per90 === null) return '-'
    return per90.toFixed(2)
  }

  return (
    <table className="table" style={{ marginTop: 8 }}>
      <thead>
        <tr>
          {COLUMNS.map((col) => (
            <th
              key={col.key}
              onClick={() => handleSort(col.key)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {showPer90 && col.per90Label ? col.per90Label : col.label}
              {getSortIndicator(col.key)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedPlayers.map((player) => {
          const windowData = player.windows[window]
          const minutes = windowData?.minutes ?? 0
          const fouls = windowData?.fouls_committed ?? 0
          const drawn = windowData?.fouls_drawn ?? 0
          const yellows = windowData?.yellows ?? 0
          const reds = windowData?.reds ?? 0

          return (
            <tr key={player.player_id}>
              <td>{player.name}</td>
              <td>{player.position?.charAt(0) || '-'}</td>
              <td>{windowData?.matches ?? 0}</td>
              <td>{minutes}</td>
              <td>{showPer90 ? formatPer90(fouls, minutes, windowData?.fouls_per_90) : fouls}</td>
              <td>{showPer90 ? formatPer90(drawn, minutes) : drawn}</td>
              <td style={{ color: yellows > 0 ? 'var(--yellow)' : undefined }}>
                {showPer90 ? formatPer90(yellows, minutes, windowData?.yellows_per_90) : yellows}
                <TrendArrow trend={windowData?.yellows_trend ?? null} show={showTrend} />
              </td>
              <td style={{ color: reds > 0 ? 'var(--red)' : undefined }}>
                {showPer90 ? formatPer90(reds, minutes) : reds}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/**
 * Combined player tables for both teams
 */
interface PlayerCardTablesProps {
  homePlayers: PlayerCardRow[]
  awayPlayers: PlayerCardRow[]
  homeTeam: TeamLite
  awayTeam: TeamLite
  window: CardWindowKey
  lineupConfirmed?: boolean
}

export function PlayerCardTables({
  homePlayers,
  awayPlayers,
  homeTeam,
  awayTeam,
  window,
  lineupConfirmed = false,
}: PlayerCardTablesProps) {
  const [showPer90, setShowPer90] = useState(false)

  const allPlayers = [...homePlayers, ...awayPlayers]
  const hasHomeXI = homePlayers.some((p) => p.is_xi)
  const hasAwayXI = awayPlayers.some((p) => p.is_xi)
  const hasHomeBench = homePlayers.some((p) => !p.is_xi)
  const hasAwayBench = awayPlayers.some((p) => !p.is_xi)
  const hasXI = hasHomeXI || hasAwayXI
  const hasBench = hasHomeBench || hasAwayBench

  const xiLabel = lineupConfirmed ? 'Confirmed XI' : 'Predicted XI'
  const xiPill = lineupConfirmed ? 'confirmed' : 'predicted'
  const benchLabel = lineupConfirmed ? 'Confirmed Bench' : 'Predicted Bench'

  return (
    <>
      {/* Starting XI section */}
      {hasXI && (
        <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
          <div className="card-inner">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <strong>Player Cards - {xiLabel}</strong>
                <span
                  className="pill"
                  style={lineupConfirmed ? { background: 'var(--green)', color: 'var(--bg)' } : undefined}
                >
                  {xiPill}
                </span>
              </div>
              {/* Per/90 toggle */}
              <button
                className={`chip ${showPer90 ? 'chip-active' : ''}`}
                onClick={() => setShowPer90(!showPer90)}
                style={{ fontSize: 11 }}
              >
                Per 90
              </button>
            </div>

            {/* Home team */}
            {hasHomeXI && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                  {homeTeam.crest_url && <img src={homeTeam.crest_url} alt="" style={{ width: 20, height: 20 }} />}
                  <span style={{ fontWeight: 600 }}>{homeTeam.name}</span>
                </div>
                <PlayerCardTable
                  players={allPlayers}
                  window={window}
                  team={homeTeam}
                  showXI={true}
                  showPer90={showPer90}
                />
              </div>
            )}

            {/* Away team */}
            {hasAwayXI && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                  {awayTeam.crest_url && <img src={awayTeam.crest_url} alt="" style={{ width: 20, height: 20 }} />}
                  <span style={{ fontWeight: 600 }}>{awayTeam.name}</span>
                </div>
                <PlayerCardTable
                  players={allPlayers}
                  window={window}
                  team={awayTeam}
                  showXI={true}
                  showPer90={showPer90}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bench section */}
      {hasBench && (
        <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
          <div className="card-inner">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
              <strong>Player Cards - {benchLabel}</strong>
              <span className="pill">bench</span>
            </div>

            {/* Home team */}
            {hasHomeBench && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                  {homeTeam.crest_url && <img src={homeTeam.crest_url} alt="" style={{ width: 20, height: 20 }} />}
                  <span style={{ fontWeight: 600 }}>{homeTeam.name}</span>
                </div>
                <PlayerCardTable
                  players={allPlayers}
                  window={window}
                  team={homeTeam}
                  showXI={false}
                  showPer90={showPer90}
                />
              </div>
            )}

            {/* Away team */}
            {hasAwayBench && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                  {awayTeam.crest_url && <img src={awayTeam.crest_url} alt="" style={{ width: 20, height: 20 }} />}
                  <span style={{ fontWeight: 600 }}>{awayTeam.name}</span>
                </div>
                <PlayerCardTable
                  players={allPlayers}
                  window={window}
                  team={awayTeam}
                  showXI={false}
                  showPer90={showPer90}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* No players fallback */}
      {!hasXI && !hasBench && (
        <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
          <div className="card-inner">
            <div className="small" style={{ textAlign: 'center', color: 'var(--muted)', padding: '20px 0' }}>
              No player data available
            </div>
          </div>
        </div>
      )}
    </>
  )
}
